import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	CircularProgress,
	Grid2,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Group, Panel, Separator } from "react-resizable-panels";

import { ATHENS_GRAY } from "@/shared/colors";
import { Product, PRODUCT_AREA_ASSET_ID } from "@/shared/models/Product";
import {
	getProductDetail,
	getProducts,
} from "@/shared/repositories/ProductRepository";
import { DrawingViewer } from "@/pages/products/components/DrawingViewer";
import { WeldlineSetupView } from "./components/WeldlineSetupView";

export const WeldlineSetupPage = () => {
	const { productId } = useParams<{ productId: string }>();
	const navigate = useNavigate();

	const [error, setError] = useState("");

	const { data: allProducts, isLoading: isLoadingProducts } = useQuery<
		Product[],
		Error
	>({
		queryKey: ["products"],
		queryFn: () => getProducts(),
	});

	const products = allProducts?.filter(
		(p) => p.AreaAssetId === PRODUCT_AREA_ASSET_ID.weld,
	);

	const { data: productDetail, isLoading: isLoadingDetail } = useQuery<
		Product,
		Error
	>({
		queryKey: ["productDetail", productId],
		queryFn: () => getProductDetail(Number(productId), 10),
		enabled: !!productId && !isNaN(Number(productId)),
	});

	const handleProductSelect = (product: Product | null) => {
		if (product?.ProductId) {
			navigate(`/weldline-setup/${product.ProductId}`);
		}
	};

	const selectedProduct =
		products?.find((p) => p.ProductId === Number(productId)) || null;

	return (
		<>
			<HelmetDexteel title="165 Weldline Setup" />
			<Box
				sx={{
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
					backgroundColor: ATHENS_GRAY,
				}}
			>
				{/* Header */}
				<Box sx={{ px: 2, pt: 2, pb: 1 }}>
					<Grid2 container spacing={1}>
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="h5" fontWeight={600}>
								165 WELDLINE SETUP
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Paper
								elevation={1}
								sx={{
									p: 2,
									display: "flex",
									alignItems: "center",
								}}
							>
								<Autocomplete
									id="product-selector"
									fullWidth
									clearOnEscape
									options={products || []}
									getOptionLabel={(option) =>
										`${option.PartNumber}${
											option.ExtendedPartNumber &&
											option.ExtendedPartNumber !== option.PartNumber
												? ` (${option.ExtendedPartNumber})`
												: ""
										}${option.GenericName ? ` - ${option.GenericName}` : ""}${option.CustomerName ? ` - ${option.CustomerName}` : ""}`
									}
									loading={isLoadingProducts}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Select Product"
											size="small"
											InputProps={{
												...params.InputProps,
												endAdornment: (
													<>
														{isLoadingProducts ? (
															<CircularProgress color="inherit" size={20} />
														) : (
															params.InputProps.endAdornment
														)}
													</>
												),
											}}
										/>
									)}
									value={selectedProduct}
									onChange={(_, newValue) => handleProductSelect(newValue)}
									isOptionEqualToValue={(option, value) =>
										option.ProductId === value.ProductId
									}
								/>
							</Paper>
						</Grid2>
					</Grid2>
				</Box>

				{/* Splitter Content */}
				<Box sx={{ flex: 1, overflow: "hidden", px: 2, pb: 2 }}>
					{isLoadingDetail && productId ? (
						<Box
							sx={{
								height: "100%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgress />
						</Box>
					) : (
						<Group orientation="horizontal" style={{ height: "100%" }}>
							{/* Left Panel - Read-only View */}
							<Panel id="form-panel" defaultSize={50} minSize={40}>
								<Paper
									elevation={2}
									sx={{ height: "100%", overflow: "hidden" }}
								>
									{productDetail && (
										<WeldlineSetupView product={productDetail} />
									)}
								</Paper>
							</Panel>

							{/* Resize Handle */}
							<Separator
								style={{
									width: 8,
									backgroundColor: "#e0e0e0",
									cursor: "col-resize",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Box
									sx={{
										width: 4,
										height: 40,
										backgroundColor: "#bdbdbd",
										borderRadius: 2,
									}}
								/>
							</Separator>

							{/* Right Panel - Drawing Viewer */}
							<Panel id="drawing-panel" defaultSize={50} minSize={20}>
								<Paper
									elevation={2}
									sx={{
										height: "100%",
										overflow: "hidden",
									}}
								>
									<DrawingViewer
										drawingPath={productDetail?.DrawingPath || null}
										toolbarLeft={
											<TextField
												value={productDetail?.DrawingNumber ?? ""}
												label="Drawing Number"
												size="small"
												disabled
												sx={{ minWidth: 180 }}
											/>
										}
									/>
								</Paper>
							</Panel>
						</Group>
					)}
				</Box>
			</Box>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
